import { Box } from '@mui/material';
import React, {
  cloneElement,
  isValidElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import PageHeader from '../PageHeader';
import { cellFormat } from '../Table/cellFormat';
import ProcessDialogContent from './ProcessDialogContent';
import ProcessDialogFooter from './ProcessDialogFooter';
import ProcessDialogHeader from './ProcessDialogHeader';
import ProcessDialogRoot from './ProcessDialogRoot';
import type {
  ProcessDialogContentVariant,
  ProcessDialogWithTabsHeaderProps,
} from './types';
import { PROCESS_DIALOG_CONTENT_PADDING } from './types';

export interface ProcessDialogSimpleProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  headerActions?: ReactNode;
  showFooter?: boolean;
  contentVariant?: ProcessDialogContentVariant;
  contentPadding?: number | string;
}

export const ProcessDialogSimple: React.FC<ProcessDialogSimpleProps> = ({
  open,
  onClose,
  title,
  children,
  headerActions,
  showFooter = true,
  contentVariant,
  contentPadding,
}) => {
  return (
    <ProcessDialogRoot open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <ProcessDialogHeader
          title={title}
          onClose={onClose}
          actions={headerActions}
        />
        <ProcessDialogContent variant={contentVariant} padding={contentPadding}>
          {children}
        </ProcessDialogContent>
        {showFooter && <ProcessDialogFooter />}
      </Box>
    </ProcessDialogRoot>
  );
};

function getHorizontalPadding(padding: number | string): number | string {
  const parts = String(padding).trim().split(/\s+/);
  return parts.length >= 2 ? parts[1] : parts[0];
}

export const ProcessDialogWithTabsHeader: React.FC<
  ProcessDialogWithTabsHeaderProps
> = ({
  open,
  onClose,
  title,
  status,
  tabs,
  initialIndex = 0,
  headerActions = [],
  contentVariant = 'comfortable',
  contentPadding,
}) => {
  const safeInitial = Math.min(
    Math.max(0, initialIndex),
    Math.max(0, tabs.length - 1),
  );
  const [activeIndex, setActiveIndex] = useState(safeInitial);

  useEffect(() => {
    const next = Math.min(
      Math.max(0, initialIndex),
      Math.max(0, tabs.length - 1),
    );
    setActiveIndex(next);
  }, [open, initialIndex, tabs.length]);

  const activeValue = String(activeIndex);
  const sizedTitle = isValidElement(title)
    ? cloneElement(title as React.ReactElement<{ noExpand?: boolean }>, {
        noExpand: true,
      })
    : title;

  const headerTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {sizedTitle}
      {status != null && (
        <Box sx={{ px: 0.5, py: 0.25 }}>
          {cellFormat(status ?? 'Nuevo', 'status-chip')}
        </Box>
      )}
    </Box>
  );

  const toggleTabs =
    tabs.length > 0
      ? tabs.map((tab, index) => ({
          id: String(index),
          label: tab.label,
          value: String(index),
          disabled: tab.disabled,
        }))
      : undefined;

  const resolvedContentPadding =
    contentPadding ?? PROCESS_DIALOG_CONTENT_PADDING[contentVariant];
  const tabsHorizontalPadding = getHorizontalPadding(resolvedContentPadding);

  const TAB_VERTICAL_PADDING = '12px';
  const finalContentPadding =
    tabs.length > 0
      ? `${TAB_VERTICAL_PADDING} ${tabsHorizontalPadding}`
      : resolvedContentPadding;

  const activeContent =
    tabs.length > 0 && activeIndex >= 0 && activeIndex < tabs.length
      ? tabs[activeIndex].content
      : null;

  return (
    <ProcessDialogRoot open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <ProcessDialogHeader title={headerTitle} onClose={onClose} />
        {toggleTabs != null && (
          <Box
            sx={{
              px: tabsHorizontalPadding,
              pt: TAB_VERTICAL_PADDING,
              pb: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <PageHeader.ToggleGroup
              tabs={toggleTabs}
              value={activeValue}
              onChange={(v) => setActiveIndex(Number(v))}
            />
            {headerActions.length > 0 && (
              <PageHeader.ActionButtons buttons={headerActions} />
            )}
          </Box>
        )}
        <ProcessDialogContent padding={finalContentPadding}>
          {activeContent}
        </ProcessDialogContent>
        <ProcessDialogFooter />
      </Box>
    </ProcessDialogRoot>
  );
};
